import React, { useId } from "react";

import { useTranslation } from "lib/i18n/use-translation";

import styles from "./business-impact.module.scss";
import {
  formatDayMonth,
  formatFullDay,
  formatMonth,
  formatNumber,
  formatPercent,
} from "./business-impact-view";
import ProgressBlock from "./ProgressBlock";
import { PilotSample } from "./sample";

const K = "admin:companyDashboard.businessImpact.pilot";

// The dark summary card for a client still in its pilot: no ROI figure yet,
// so it shows how far the pilot has come and what it has proved so far.
const PilotSummary = ({ sample }: { sample: PilotSample }) => {
  const { t, i18n } = useTranslation(["admin"]);
  const locale = i18n.language;
  const { progress, period, proofPoints } = sample;
  const review = formatMonth(progress.review, locale);
  const proofId = useId();

  return (
    <section className={styles["summary"]}>
      <div className={styles["summary-roi"]}>
        <div className={styles["eyebrow"]}>
          <h3 className={styles["eyebrow-heading"]}>
            {t(`${K}.eyebrow`, { month: progress.month })}
          </h3>
          <span className={styles["pilot-pill"]}>{t(`${K}.pill`)}</span>
        </div>
        <p className={styles["pilot-intro"]}>
          {t(`${K}.intro`, { of: progress.of, review })}
        </p>
        <ProgressBlock
          label={t(`${K}.progressLabel`)}
          month={progress.month}
          of={progress.of}
          valueText={t(
            "admin:companyDashboard.businessImpact.summary.progressMonth",
            progress
          )}
          note={t(`${K}.progressNote`, {
            from: formatDayMonth(period.from, locale),
            to: formatFullDay(period.to, locale),
            review,
          })}
        />
      </div>
      <div className={styles["summary-drivers"]}>
        <p id={proofId} className={styles["eyebrow"]}>
          {t(`${K}.proofEyebrow`)}
        </p>
        <ul className={styles["proofs"]} aria-labelledby={proofId}>
          {proofPoints.map((point) => {
            const figure =
              "percent" in point
                ? formatPercent(point.percent, locale)
                : formatNumber(point.count, locale);
            return (
              <li key={point.id} className={styles["proof"]}>
                <span className={styles["proof-figure"]}>{figure}</span>
                <span className={styles["proof-text"]}>
                  {t(
                    `${K}.proofs.${point.id}`,
                    "count" in point ? { count: point.count } : {}
                  )}
                </span>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
};

export default PilotSummary;
